import { useEffect, useState } from 'react';
import { KeyValueList, Panel, Toolbar } from './Page';
import { CheckboxField, TextField, ToggleField } from './Form';
import { CardSkeleton, ErrorState, InlineError } from './Feedback';
import { useAsync } from '../lib/hooks';
import { useToast } from '../lib/toast';
import { errorMessage } from '../lib/api';
import { vendorLearning } from '../lib/endpoints';
import { formatDateTime, formatRelative, fromDateInputValue, toDateInputValue } from '../lib/format';
import type { VendorLearningDetailDto } from '../lib/types';

/**
 * AI LEARNING, ON THE SUPPLIER'S OWN CARD — the same placement principle as
 * {@link ./VendorTeachingPanel VendorTeachingPanel} just above it: an operator turning learning on
 * for a supplier already has that supplier open, and sending them to a separate global "AI
 * Learning" screen to find the same vendor again loses the context that was never in doubt.
 *
 * Mirrors the Business Central MultiEntity ToolKit's own "Vendor AI Learning" card, reached from
 * the Vendor Card itself: one enable switch per vendor, a hard prerequisite that the vendor's
 * invoice layout has been taught (via the "Invoice template" panel above this one) before any
 * history may be published, and — once both are true — the historical-data scope and the publish
 * action, all bound to this one vendor.
 */
export function VendorLearningPanel({
  qboVendorId,
  vendorName,
}: {
  qboVendorId: string;
  vendorName: string | null;
}): JSX.Element {
  const toast = useToast();
  const state = useAsync((signal) => vendorLearning.get(qboVendorId, signal), [qboVendorId]);

  const [busy, setBusy] = useState<string | null>(null);
  const [draft, setDraft] = useState<VendorLearningDetailDto | null>(null);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (state.data && !dirty) setDraft(state.data);
  }, [state.data, dirty]);

  const set = <K extends keyof VendorLearningDetailDto>(key: K, value: VendorLearningDetailDto[K]) => {
    setDraft((current) => (current ? { ...current, [key]: value } : current));
    setDirty(true);
  };

  const run = async <T,>(name: string, action: () => Promise<T>, onSuccess: (result: T) => void) => {
    setBusy(name);
    try {
      const result = await action();
      onSuccess(result);
    } catch (caught) {
      toast.error(`${name} did not complete`, errorMessage(caught));
    } finally {
      setBusy(null);
    }
  };

  const save = () => {
    if (!draft) return;
    void run(
      'Save',
      () =>
        vendorLearning.setConsent(qboVendorId, {
          enabledForLearning: draft.enabledForLearning,
          sendHistoricalLearning: draft.sendHistoricalLearning,
          historyStartDate: fromDateInputValue(toDateInputValue(draft.historyStartDate)),
          includeBills: draft.includeBills,
          includePurchases: draft.includePurchases,
          includeVendorCredits: draft.includeVendorCredits,
          includeAttachments: draft.includeAttachments,
          maxAttachmentSamples: draft.maxAttachmentSamples,
        }),
      (updated) => {
        toast.success('Saved', `AI Learning updated for ${updated.vendorName ?? vendorName ?? qboVendorId}.`);
        state.setData(updated);
        setDraft(updated);
        setDirty(false);
      },
    );
  };

  const turnOff = () => {
    void run(
      'Turn off',
      () =>
        vendorLearning.setConsent(qboVendorId, {
          enabledForLearning: false,
          sendHistoricalLearning: false,
          historyStartDate: null,
          includeBills: true,
          includePurchases: true,
          includeVendorCredits: true,
          includeAttachments: false,
          maxAttachmentSamples: 10,
        }),
      (updated) => {
        toast.success('AI Learning is off for this vendor');
        state.setData(updated);
        setDraft(updated);
        setDirty(false);
      },
    );
  };

  const publish = () => {
    void run(
      'Publish',
      () => vendorLearning.publish(qboVendorId),
      (outcome) => {
        if (outcome.success) {
          toast.success(
            'Published',
            `${outcome.documentsPublished} document(s) in ${outcome.batchesSent} batch(es)`
              + (outcome.attachmentsPublished > 0 ? `, ${outcome.attachmentsPublished} attachment sample(s).` : '.'),
          );
        } else {
          toast.error('Publish did not complete', outcome.message ?? outcome.errorCode ?? 'Refused.');
        }
        state.reload();
      },
    );
  };

  if (state.loading && !draft) return <CardSkeleton />;

  if (state.error && !draft) {
    return <ErrorState message="AI Learning setup could not be read." detail={state.error} onRetry={state.reload} />;
  }

  const d = draft;
  if (!d) return <></>;

  return (
    <div className="stack">
      <ToggleField
        label={d.enabledForLearning ? 'AI Learning is on for this vendor' : 'AI Learning is off for this vendor'}
        checked={d.enabledForLearning}
        disabled={busy !== null}
        onChange={(checked) => (checked ? set('enabledForLearning', true) : turnOff())}
      />

      {d.layoutLearned ? (
        <div className="banner banner-good" role="status">
          <div className="banner-body">
            <p className="banner-title">Invoice layout taught</p>
            <p className="banner-detail">
              {d.layoutLearnedUtc
                ? `Confirmed to iERP AI on ${formatDateTime(d.layoutLearnedUtc)}, from an approved invoice above.`
                : 'Confirmed to iERP AI from an approved invoice above.'}
            </p>
          </div>
        </div>
      ) : (
        <div className="banner banner-info" role="status">
          <div className="banner-body">
            <p className="banner-title">Invoice layout not taught yet</p>
            <p className="banner-detail">
              Improves how future invoices from this supplier are read — drop and approve one in the Invoice
              template panel above whenever you have one. It is not required to Publish this vendor's posting
              history below, which a vendor whose invoices only ever arrive through a trading-partner handshake
              will never have one to give.
            </p>
          </div>
        </div>
      )}

      {d.enabledForLearning ? (
        <Panel title="Send historical learning data" bodyClassName="form-grid">
          <ToggleField
            label="Send Historical Learning"
            checked={d.sendHistoricalLearning}
            onChange={(checked) => set('sendHistoricalLearning', checked)}
          />
          <TextField
            label="History start date"
            type="date"
            value={toDateInputValue(d.historyStartDate)}
            onChange={(value) => set('historyStartDate', fromDateInputValue(value))}
            hint="Leave blank to publish this vendor's whole history."
            disabled={!d.sendHistoricalLearning}
          />
          <CheckboxField
            label="Include Bills"
            checked={d.includeBills}
            onChange={(checked) => set('includeBills', checked)}
            disabled={!d.sendHistoricalLearning}
          />
          <CheckboxField
            label="Include Expenses (Purchases)"
            checked={d.includePurchases}
            onChange={(checked) => set('includePurchases', checked)}
            disabled={!d.sendHistoricalLearning}
          />
          <CheckboxField
            label="Include Vendor Credits"
            checked={d.includeVendorCredits}
            onChange={(checked) => set('includeVendorCredits', checked)}
            disabled={!d.sendHistoricalLearning}
          />
          <CheckboxField
            label="Include attachment content"
            checked={d.includeAttachments}
            onChange={(checked) => set('includeAttachments', checked)}
            hint="Only attachments already retrieved for this company can be included."
            disabled={!d.sendHistoricalLearning}
          />
          <TextField
            label="Maximum invoice samples"
            type="number"
            value={String(d.maxAttachmentSamples)}
            onChange={(value) => {
              const parsed = Number(value);
              if (Number.isFinite(parsed)) set('maxAttachmentSamples', Math.min(25, Math.max(1, Math.round(parsed))));
            }}
            hint="Caps how many attachment samples one publish sends for this vendor (1-25)."
            disabled={!d.sendHistoricalLearning || !d.includeAttachments}
          />

          <Toolbar>
            <button type="button" className="btn btn-primary" disabled={busy !== null || !dirty} onClick={save}>
              {busy === 'Save' ? 'Saving…' : 'Save'}
            </button>
          </Toolbar>
        </Panel>
      ) : null}

      {d.consentGrantedByEmail ? (
        <p className="panel-footnote">
          Granted by {d.consentGrantedByEmail}
          {d.consentGrantedUtc ? ` on ${formatDateTime(d.consentGrantedUtc)}` : ''}.
        </p>
      ) : null}

      <Panel title="Publish">
        <KeyValueList columns={1}>
          <div className="kv">
            <dt className="kv-label">Ready to publish</dt>
            <dd className="kv-value">{d.unpublishedCount} document(s)</dd>
          </div>
          <div className="kv">
            <dt className="kv-label">Last published</dt>
            <dd className="kv-value">
              {d.lastPublishedUtc ? formatRelative(d.lastPublishedUtc) : 'never'}
              {d.publishedDocumentCount > 0 ? (
                <span className="cell-sub">{d.publishedDocumentCount} document(s) total</span>
              ) : null}
            </dd>
          </div>
        </KeyValueList>
        {d.lastPublishMessage ? (
          d.lastPublishSucceeded ? (
            <p className="field-hint" role="status">
              {d.lastPublishMessage}
            </p>
          ) : (
            <InlineError message={d.lastPublishMessage} />
          )
        ) : null}
        <Toolbar>
          <button type="button" className="btn btn-primary" disabled={busy !== null} onClick={publish}>
            {busy === 'Publish' ? 'Publishing…' : 'Publish now'}
          </button>
        </Toolbar>
        <p className="field-hint">
          Refused until AI Learning is on and Send Historical Learning is on — the licence gate itself is only
          checked at the moment of this click. An invoice layout is not required.
        </p>
      </Panel>
    </div>
  );
}
